import { useCallback } from "react";
import { useToast } from "../../../../contexts-hooks/hooks/useToster";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { debounce } from "lodash";
import {
  useAllInterview,
  useCancelCombinedById,
  combinedReportById,
} from "../../../../store/query/scheduleInterviewQuery";
import moment from "moment";
import { mergePdfBlobs } from "../../../../utils/pdfMerger";
const useScheduleInterviewList = () => {
  const [filterName, setFilterName] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candiName, setCandiName] = useState(null);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [cancelLoading, setCancelLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState("All");
  const [customDate, setCustomDate] = useState(null);

  const getDateRange = (filter) => {
    switch (filter) {
      case "Yesterday":
        return {
          startDate: moment().subtract(1, "day").startOf("day").toISOString(),
          endDate: moment().subtract(1, "day").endOf("day").toISOString(),
        };
      case "Today":
        return {
          startDate: moment().startOf("day").toISOString(),
          endDate: moment().endOf("day").toISOString(),
        };
      case "Tomorrow":
        return {
          startDate: moment().add(1, "day").startOf("day").toISOString(),
          endDate: moment().add(1, "day").endOf("day").toISOString(),
        };
      case "Custom":
        return {
          startDate: customDate
            ? moment(customDate).startOf("day").toISOString()
            : "",
          endDate: customDate
            ? moment(customDate).endOf("day").toISOString()
            : "",
        };
      default:
        return { startDate: "", endDate: "" };
    }
  };

  const dateRange = getDateRange(dateFilter);

  const [filterOptions, setFilterOptions] = useState({
    page: 1,
    limit: 20,
    name: "",
    status: "All",
    interviewType: "All",
  });
  const interview = useAllInterview({
    page: filterOptions.page,
    limit: filterOptions.limit,
    status: filterOptions.status,
    search: filterOptions.name,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    interviewType: filterOptions.interviewType,
  });

  const headCells = [
    {
      numeric: false,
      disablePadding: true,
      label: "Candidate Name",
    },
    {
      numeric: false,
      disablePadding: true,
      label: "Recruiter Name",
    },
    {
      numeric: false,
      disablePadding: false,
      label: "Sourcer",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Phone Number",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Apply for",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Start Date",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "End Date",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Address",
    },
    {
      numeric: false,
      disablePadding: false,
      label: "Interview Type",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Status",
    },
    {
      numeric: true,
      disablePadding: false,
      label: "Action",
    },
  ];

  const [selectedType, setSelectedType] = useState("AI Bot");
  const { mutate: cancelCombined } = useCancelCombinedById();
  const handleCancel = () => {
    setCancelLoading(true);
    if (selectedCandidate) {
      cancelCombined(
        { id: selectedCandidate, interviewType: selectedType },
        {
          onSuccess: () => {
            setCancelLoading(false);
            setOpen(false);
            showToast("Interview cancelled successfully!");
          },
          onError: () => {
            setCancelLoading(false);
            showToast("Failed to cancel interview!", "error");
          },
        },
      );
    }
  };
  const handleFormateDate = (isoString) => {
    return moment(isoString).format("MMMM Do YYYY, h:mm:ss a");
  };

  const [downloading, setDownloading] = useState({});

  const downloadBlob = (blobData, filename) => {
    const url = window.URL.createObjectURL(blobData);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const handleExportInterviewReport = async (row) => {
    const candidateId = row?._id;
    if (!candidateId) return;

    const isAiBotPlus = row?.interviewType === "AI Bot +" && row?.aiInterviewId;
    const candidateName = row?.candidateName || "candidate";

    setDownloading((prev) => ({ ...prev, [candidateId]: true }));
    let genTimer = setTimeout(() => {
      showToast("Report is being generated. Please wait...", "info");
    }, 1500);

    try {
      if (isAiBotPlus) {
        let codingBlobData = null;
        let aiBlobData = null;

        try {
          codingBlobData = await combinedReportById({
            id: candidateId,
            interviewType: row?.interviewType || "AI Bot",
          });
        } catch (err) {
          console.error("Coding report download failed:", err);
        }

        try {
          aiBlobData = await combinedReportById({
            id: row.aiInterviewId,
            interviewType: "AI Bot",
          });
        } catch (err) {
          console.error("AI report download failed:", err);
        }

        if (!codingBlobData && !aiBlobData) {
          throw new Error("Neither coding nor AI report is ready yet");
        }

        const mergedBlobData = await mergePdfBlobs(codingBlobData, aiBlobData);
        if (!mergedBlobData || mergedBlobData.size === 0) {
          throw new Error("Failed to generate combined PDF report");
        }

        downloadBlob(mergedBlobData, `candidate_report_${candidateName}.pdf`);

        const wasOnDemand = codingBlobData?.wasGenerated || aiBlobData?.wasGenerated;
        if (codingBlobData && aiBlobData) {
          showToast(
            wasOnDemand
              ? "Combined report generated and downloaded successfully!"
              : "Combined report downloaded successfully!",
            "success"
          );
        } else if (codingBlobData) {
          showToast(
            "Coding report downloaded. AI interview report is not ready yet.",
            "warning",
          );
        } else {
          showToast(
            "AI Interview report downloaded. Coding report is not ready yet.",
            "warning",
          );
        }
      } else {
        const codingBlobData = await combinedReportById({
          id: candidateId,
          interviewType: row?.interviewType || "AI Bot",
        });
        if (!codingBlobData || codingBlobData.size === 0) {
          throw new Error("Received empty file from server");
        }
        downloadBlob(codingBlobData, `candidate_report_${candidateName}.pdf`);
        showToast(
          codingBlobData.wasGenerated
            ? "Report generated and downloaded successfully!"
            : "Report downloaded successfully!",
          "success"
        );
      }
    } catch (e) {
      console.error("Download failed:", e);
      showToast(
        e?.message || "Failed to download the report. The report is not ready yet.",
        "error",
      );
    } finally {
      clearTimeout(genTimer);
      setDownloading((prev) => ({ ...prev, [candidateId]: false }));
    }
  };

  const handleClickOpen = (candidateId, candidate_name, interviewType) => {
    setSelectedCandidate(candidateId);
    setCandiName(candidate_name);
    setSelectedType(interviewType || "AI Bot");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const debouncedFilterChange = useCallback(
    debounce((value) => {
      setFilterOptions((prev) => ({
        ...prev,
        name: value,
      }));
    }, 1000),
    [],
  );

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setFilterName(value);
    debouncedFilterChange(value);
  };

  const handleStatusChange = (event) =>
    setFilterOptions((prev) => ({
      ...prev,
      page: 1,
      status: event.target.value,
    }));

  const handleInterviewTypeChange = (event) =>
    setFilterOptions((prev) => ({
      ...prev,
      page: 1,
      interviewType: event.target.value,
    }));

  const handleDateFilterChange = (event) => {
    const value = event.target.value;
    setDateFilter(value);
    if (value !== "Custom") {
      setCustomDate(null);
    }
    setFilterOptions((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleCustomDateChange = (date) => {
    setCustomDate(date);
    setFilterOptions((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const handleRowsPerPageChange = (event) =>
    setFilterOptions((prev) => ({
      ...prev,
      limit: event.target.value,
    }));

  const handlePrevPage = () => {
    if (filterOptions.page > 1) {
      setFilterOptions((prev) => ({
        ...prev,
        page: prev.page - 1,
      }));
    }
  };

  const handleNextPage = () => {
    if (interview.data?.data.length > 0) {
      setFilterOptions((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  const handleEditCandidate = (candidate, interviewType) => {
    if (interviewType === "Coding Test" || interviewType === "AI Bot +") {
      navigate("/app/interview/edit-schedule-interview", {
        state: { selectedCandidate: candidate, interviewType },
      });
    } else {
      navigate("/app/interview/edit-schedule-interview", {
        state: { selectedCandidate: candidate },
      });
    }
  };

  const handleResetFilters = () => {
    setFilterName("");
    setDateFilter("All");
    setCustomDate(null);
    setFilterOptions((prev) => ({
      ...prev,
      page: 1,
      name: "",
      status: "All",
      interviewType: "All",
    }));
  };

  return {
    filterName,
    open,
    candiName,
    filterOptions,
    cancelLoading,
    interview,
    headCells,
    dateFilter,
    customDate,
    handleClose,
    handleClickOpen,
    handleFilterChange,
    handleStatusChange,
    handleInterviewTypeChange,
    handleDateFilterChange,
    handleCustomDateChange,
    handleRowsPerPageChange,
    handlePrevPage,
    handleNextPage,
    handleEditCandidate,
    handleCancel,
    handleFormateDate,
    handleExportInterviewReport,
    downloading,
    handleResetFilters,
  };
};

export default useScheduleInterviewList;
